/**
 * FormBuilderWithLayout
 * FormBuilder with layout configuration support
 */

import * as React from 'react';
import { cn } from '../lib/utils';
import { FieldRenderer } from './FieldRenderer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import type { ElementSchema, FieldSchema, ValidationResult } from '../framework/ElementSchema';
import type { FormLayoutConfig, SectionLayout, TabLayout } from './LayoutConfig';
import { defaultLayoutConfig, getUnsectionedFields } from './LayoutConfig';
import type { KitTranslateFn } from '../lib/kitTranslate';

export interface FormBuilderWithLayoutProps<TData = Record<string, unknown>> {
    /** Element schema defining the form */
    schema: ElementSchema<TData>;
    /** Current form values */
    value: TData;
    /** Called when values change */
    onChange: (value: TData) => void;
    /** Layout configuration */
    layout?: FormLayoutConfig;
    /** Validation errors */
    errors?: Record<string, string>;
    /** Whether the form is disabled */
    disabled?: boolean;
    /** Additional class name */
    className?: string;
    translate?: KitTranslateFn;
}

/**
 * FormBuilderWithLayout - Generates a form from schema with layout configuration
 */
export function FormBuilderWithLayout<TData = Record<string, unknown>>({
    schema,
    value,
    onChange,
    layout = defaultLayoutConfig,
    errors = {},
    disabled = false,
    className,
    translate,
}: FormBuilderWithLayoutProps<TData>): React.ReactElement {
    // Get all field names
    const allFieldNames = React.useMemo(
        () => schema.fields.filter((f) => !f.hidden).map((f) => f.name),
        [schema.fields]
    );

    // Get field by name
    const getField = React.useCallback(
        (name: string): FieldSchema | undefined => schema.fields.find((f) => f.name === name),
        [schema.fields]
    );

    // Update a single field
    const updateField = React.useCallback(
        (fieldName: string, fieldValue: unknown) => {
            onChange({
                ...value,
                [fieldName]: fieldValue,
            } as TData);
        },
        [value, onChange]
    );

    // Get gap class
    const gapClass = React.useMemo(() => {
        switch (layout.gap) {
            case 'sm':
                return 'gap-2';
            case 'lg':
                return 'gap-6';
            default:
                return 'gap-4';
        }
    }, [layout.gap]);

    // Get columns class
    const getColumnsClass = (columns?: number) => {
        switch (columns) {
            case 2:
                return 'grid-cols-1 md:grid-cols-2';
            case 3:
                return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
            case 4:
                return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';
            default:
                return 'grid-cols-1';
        }
    };

    // Render a single field
    const renderField = (fieldName: string) => {
        const field = getField(fieldName);
        if (!field || field.hidden) return null;

        return (
            <FieldRenderer
                key={fieldName}
                field={field}
                value={(value as Record<string, unknown>)[fieldName]}
                onChange={(v) => updateField(fieldName, v)}
                error={errors[fieldName]}
                disabled={disabled}
                translate={translate}
            />
        );
    };

    // Render fields in a grid
    const renderFieldsGrid = (fieldNames: string[], columns?: number) => (
        <div className={cn('grid', gapClass, getColumnsClass(columns || layout.columns))}>
            {fieldNames.map((name) => renderField(name))}
        </div>
    );

    // Render a section
    const renderSection = (section: SectionLayout, index: number) => {
        const [collapsed, setCollapsed] = React.useState(section.defaultCollapsed);
        const sectionFields = section.fields.filter((f) => allFieldNames.includes(f));

        if (sectionFields.length === 0) return null;

        if (section.collapsible) {
            return (
                <Card key={index} className={cn('overflow-hidden', section.className)}>
                    <button
                        type="button"
                        onClick={() => setCollapsed(!collapsed)}
                        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-muted/50 transition-colors"
                    >
                        <div>
                            {section.title && <h4 className="font-medium">{section.title}</h4>}
                            {section.description && !collapsed && (
                                <p className="text-sm text-muted-foreground mt-0.5">
                                    {section.description}
                                </p>
                            )}
                        </div>
                        <span className="text-muted-foreground">{collapsed ? '▸' : '▾'}</span>
                    </button>
                    {!collapsed && (
                        <CardContent className="pt-0 pb-4">
                            {renderFieldsGrid(sectionFields, section.columns)}
                        </CardContent>
                    )}
                </Card>
            );
        }

        return (
            <div key={index} className={cn('space-y-3', section.className)}>
                {(section.title || section.description) && (
                    <div className="pb-2 border-b border-border">
                        {section.title && <h4 className="font-medium text-sm">{section.title}</h4>}
                        {section.description && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                                {section.description}
                            </p>
                        )}
                    </div>
                )}
                {renderFieldsGrid(sectionFields, section.columns)}
            </div>
        );
    };

    // Render tabs
    const renderTabs = (tabs: TabLayout[]) => {
        const [activeTab, setActiveTab] = React.useState(0);

        return (
            <div className="space-y-4">
                {/* Tab buttons */}
                <div className="flex border-b border-border">
                    {tabs.map((tab, index) => (
                        <button
                            key={index}
                            type="button"
                            onClick={() => setActiveTab(index)}
                            className={cn(
                                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                                activeTab === index
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-muted-foreground hover:text-foreground'
                            )}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab content */}
                <div className="pt-2">
                    {tabs[activeTab]?.sections ? (
                        <div className={cn('space-y-6', gapClass)}>
                            {tabs[activeTab].sections!.map((section, i) =>
                                renderSection(section, i)
                            )}
                        </div>
                    ) : tabs[activeTab]?.fields ? (
                        renderFieldsGrid(
                            tabs[activeTab].fields!.filter((f) => allFieldNames.includes(f))
                        )
                    ) : null}
                </div>
            </div>
        );
    };

    // Render based on layout type
    const renderContent = () => {
        switch (layout.type) {
            case 'tabs':
                if (layout.tabs && layout.tabs.length > 0) {
                    return renderTabs(layout.tabs);
                }
                break;

            case 'sections':
                if (layout.sections && layout.sections.length > 0) {
                    const unsectioned = getUnsectionedFields(layout, allFieldNames);
                    return (
                        <div className={cn('space-y-6', gapClass)}>
                            {layout.sections.map((section, i) => renderSection(section, i))}
                            {unsectioned.length > 0 && renderFieldsGrid(unsectioned)}
                        </div>
                    );
                }
                break;

            case 'horizontal':
                return (
                    <div className="flex flex-wrap gap-4">
                        {allFieldNames.map((name) => (
                            <div key={name} className="flex-1 min-w-[200px]">
                                {renderField(name)}
                            </div>
                        ))}
                    </div>
                );
        }

        // Default: vertical
        return renderFieldsGrid(allFieldNames);
    };

    return (
        <div className={cn('form-builder-with-layout', layout.className, className)}>
            {renderContent()}
        </div>
    );
}

/**
 * Helper to create section layout
 */
export function section(fields: string[], options?: Omit<SectionLayout, 'fields'>): SectionLayout {
    return { fields, ...options };
}

/**
 * Helper to create tab layout
 */
export function tab(
    label: string,
    config: { sections?: SectionLayout[]; fields?: string[] }
): TabLayout {
    return { label, ...config };
}

export default FormBuilderWithLayout;
